<template>
  <div class="new-booking-page">
    <front-navigation></front-navigation>

    <div class="app-container">
      <div class="header-row">
        <div class="header-text">
          <h2 class="earthwerk-font">Payment Information</h2>
          <p class="subtitle">Final step — confirm and submit</p>
        </div>
        <div class="cart-btn" @click="openCartSidebar">
          <v-icon color="white" size="24">mdi-cart</v-icon>
          <span class="cart-badge" v-if="cartCount > 0">{{ cartCount }}</span>
        </div>
      </div>

      <div v-if="showMissingWarning && missingRequirements.length > 0" class="section-card warning-block">
        <div class="warning-head">
          <v-icon small color="#b26a00" class="mr-1">mdi-alert</v-icon>
          <strong>To complete your booking, please:</strong>
        </div>
        <ul class="missing-list">
          <li v-for="(item, idx) in missingRequirements" :key="idx" v-html="item"></li>
        </ul>
      </div>

      <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="postBooking">
        <div class="section-card">
          <h3 class="section-title earthwerk-font">
            <v-icon small color="#74a533" class="mr-1">mdi-account-circle</v-icon>
            Personal Information
          </h3>

          <v-layout row wrap>
            <v-flex xs12 sm6 class="pa-1">
              <v-text-field
                v-model="booking.first_name"
                label="First Name"
                :rules="requiredRules"
                :error="showMissingWarning && !booking.first_name"
                error-count="1"
                outlined
                dense
                prepend-inner-icon="mdi-account"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pa-1">
              <v-text-field
                v-model="booking.last_name"
                label="Last Name"
                :rules="requiredRules"
                :error="showMissingWarning && !booking.last_name"
                error-count="1"
                outlined
                dense
                prepend-inner-icon="mdi-account"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pa-1">
              <v-text-field
                v-model="booking.email"
                label="Email"
                type="email"
                :rules="emailRules"
                :error="showMissingWarning && !isEmailValid(booking.email)"
                error-count="1"
                outlined
                dense
                prepend-inner-icon="mdi-email"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pa-1">
              <v-autocomplete
                v-model="booking.code"
                label="Country"
                :items="allCountries"
                item-text="name"
                item-value="code"
                :rules="requiredRules"
                :error="showMissingWarning && !booking.code"
                error-count="1"
                @change="findCountryCode"
                :loading="$loadingTrue"
                outlined
                dense
                prepend-inner-icon="mdi-earth"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs12 sm6 class="pa-1">
              <v-text-field
                v-model="booking.phone"
                label="Phone (country code locked)"
                :rules="phoneRules"
                :error="showMissingWarning && !isPhoneValid(booking.phone)"
                error-count="1"
                outlined
                dense
                prepend-inner-icon="mdi-phone"
                @keydown="guardPhonePrefix"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pa-1">
              <v-text-field
                v-model="booking.city"
                label="City"
                :rules="requiredRules"
                :error="showMissingWarning && !booking.city"
                error-count="1"
                outlined
                dense
                prepend-inner-icon="mdi-city"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 class="pa-1">
              <v-text-field
                v-model="booking.promo_code"
                label="Promo Code"
                outlined
                dense
                prepend-inner-icon="mdi-ticket-percent"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </div>

        <div class="section-card">
          <div class="section-title clickable" @click="entrySectionExpanded = !entrySectionExpanded">
            <v-icon small color="#74a533" class="mr-1">mdi-map-marker</v-icon>
            Entry Information
            <v-spacer></v-spacer>
            <span class="section-hint">{{ entrySectionExpanded ? 'Collapse' : 'Expand' }}</span>
            <v-icon small color="#74a533">{{ entrySectionExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>

          <v-layout row wrap v-if="entrySectionExpanded">
            <v-flex xs12 sm6 class="pa-1">
              <v-autocomplete
                v-model="booking.entry_mode"
                label="Mode of Entry"
                :items="allEntryModes"
                item-text="entry_mode_name"
                item-value="entry_mode_public_id"
                @change="findEntryPoints"
                outlined
                dense
                prepend-inner-icon="mdi-vector-polyline"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs12 sm6 class="pa-1">
              <v-autocomplete
                v-model="booking.entry_point"
                label="Point of Entry"
                :items="allEntryPoints"
                item-text="gate_name"
                item-value="gate_public_id"
                outlined
                dense
                prepend-inner-icon="mdi-gate"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs12 class="pa-1">
              <v-text-field
                v-model="booking.mode_registration"
                label="Mode Registration Number"
                outlined
                dense
                prepend-inner-icon="mdi-numeric"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 class="pa-1">
              <v-textarea
                v-model="booking.additional_note"
                label="Additional Note"
                outlined
                dense
                rows="2"
                prepend-inner-icon="mdi-note-text"
              ></v-textarea>
            </v-flex>
          </v-layout>
        </div>

        <div v-if="proofOfResidency.length > 0" class="section-card">
          <div class="section-title clickable" @click="residency_proof = true">
            <v-icon small color="#74a533" class="mr-1">mdi-file-document</v-icon>
            Click Here To Proof Residency
          </div>

          <v-dialog v-model="residency_proof" persistent max-width="800px">
            <v-card class="residency-dialog">
              <v-card-title class="dialog-header">
                Residency Proof Details
                <v-spacer></v-spacer>
                <v-btn icon @click="residency_proof = false">
                  <v-icon color="white">mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text>
                <div v-for="(residency, key) in proofOfResidency" :key="key" class="proof-item">
                  <v-text-field readonly label="Residency" v-model="proofOfResidency[key].payment_person" outlined dense></v-text-field>
                  <v-text-field readonly label="Point Of Proof" v-model="proofOfResidency[key].proof_point" outlined dense></v-text-field>
                  <v-text-field label="First Name" v-model="proofOfResidency[key].first_name" outlined dense></v-text-field>
                  <v-text-field label="Last Name" v-model="proofOfResidency[key].last_name" outlined dense></v-text-field>
                  <v-text-field label="Document ID" v-model="proofOfResidency[key].document_id" outlined dense></v-text-field>
                </div>
              </v-card-text>
              <v-card-actions class="dialog-actions">
                <v-btn color="primary" depressed @click="addProof()" :loading="$loadingTrue">
                  <v-icon left small>mdi-check</v-icon> Add Proof
                </v-btn>
                <v-btn color="grey" text @click="residency_proof = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>

        <div class="section-card terms-section">
          <v-alert
            v-if="showMissingWarning && (!booking.terms || !booking.disclaimer)"
            type="warning"
            outlined
            color="warning"
            class="mb-3"
          >
            <strong>Please accept the required terms and liability disclaimer below.</strong>
          </v-alert>

          <div class="terms-item" :class="{ 'terms-item-error': showMissingWarning && !booking.terms }">
            <router-link :to="{ name: 'PTermsAndConditions' }" target="_blank" class="terms-link">
              <v-icon small>mdi-file-document</v-icon>
              Ol Pejeta Conservancy terms and conditions
            </router-link>
            <v-checkbox
              v-model="booking.terms"
              label="I agree to the terms and conditions"
              color="#74a533"
              hide-details
            ></v-checkbox>
            <div v-if="showMissingWarning && !booking.terms" class="terms-error-msg">
              <v-icon small color="#ff9800">mdi-alert</v-icon>
              You must agree to the terms and conditions
            </div>
          </div>

          <div class="terms-item">
            <a class="terms-link">
              <v-icon small>mdi-email-newsletter</v-icon>
              Receive Emails and Newsletters
            </a>
            <v-checkbox
              v-model="booking.newsletter"
              label="I agree to receive promotional emails"
              color="#74a533"
              hide-details
            ></v-checkbox>
          </div>

          <div class="terms-item" :class="{ 'terms-item-error': showMissingWarning && !booking.disclaimer }">
            <a class="terms-link" @click="openDisclaimer">
              <v-icon small>mdi-alert</v-icon>
              View Liability Disclaimer (Required)
            </a>
            <v-checkbox
              v-model="booking.disclaimer"
              label="I accept the liability disclaimer"
              color="#74a533"
              hide-details
            ></v-checkbox>
            <div v-if="showMissingWarning && !booking.disclaimer" class="terms-error-msg">
              <v-icon small color="#ff9800">mdi-alert</v-icon>
              You must accept the liability disclaimer
            </div>
          </div>
        </div>

        <div class="action-section">
          <button
            class="app-continue-btn"
            :disabled="processingBooking"
            @click.prevent="postBooking"
          >
            <template v-if="!processingBooking">
              <v-icon small color="white" class="mr-1">mdi-check-circle</v-icon>
              Submit Booking
            </template>
            <template v-else>
              <v-progress-circular indeterminate size="18" width="2" color="white" class="mr-2"></v-progress-circular>
              Processing...
            </template>
          </button>
        </div>
      </v-form>
    </div>

    <v-dialog v-model="showDisclaimer" persistent max-width="600px">
      <v-card class="disclaimer-card">
        <v-card-title class="dialog-header">
          <v-icon left color="white" small>mdi-alert-circle</v-icon>
          Liability Disclaimer
          <v-spacer></v-spacer>
          <v-btn icon @click="showDisclaimer = false">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="disclaimer-body">
          <p><strong>Please read the following disclaimer carefully before proceeding.</strong></p>
          <p>By entering the Ol Pejeta Conservancy ("the Conservancy") for any purpose, I acknowledge the inherent risks to my life and property.</p>
          <p>I confirm that I have read and agree to abide by the Conservancy's "Code of Conduct" to ensure my safety to a reasonable extent.</p>
          <p>I understand and accept that the Conservancy contains dangerous wild animals, including but not limited to lions, rhinos, elephants, buffaloes, snakes, and insects.</p>
          <p><strong>By entering the Conservancy, I agree to the following:</strong></p>
          <ul class="disclaimer-list">
            <li>Waive and release the Conservancy, Ol Pejeta Conservancy Limited, and its employees, agents, affiliates, and officers from any liability.</li>
            <li>Hold them harmless from any injury, claim, accident, loss, or damage, including personal injury (fatal or otherwise) and damage to personal property, resulting from my entry.</li>
            <li>This waiver applies to myself and any minors accompanying me, and is binding on my assigns, heirs, and legal representatives.</li>
          </ul>
          <p>If any portion of this waiver is deemed unenforceable, that portion shall be struck out, but the remaining provisions will remain in full force and effect.</p>
          <p><em>This waiver is governed by the laws of Kenya.</em></p>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn text @click="showDisclaimer = false">Close</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed @click="acceptDisclaimer">
            <v-icon left small>mdi-check</v-icon> Agree
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showAccountModal" persistent max-width="460px">
      <v-card class="account-card">
        <v-card-title class="dialog-header">
          <v-icon left color="white" small>mdi-account-plus-outline</v-icon>
          Create Your Guest Account
        </v-card-title>
        <v-card-text class="account-modal-body">
          <p class="account-description">
            We'll set up a guest account so you can track your booking and access it anytime.
          </p>
          <div class="account-info-block">
            <div class="account-info-row">
              <v-icon small color="#74a533">mdi-email-check-outline</v-icon>
              <span>A secure password will be sent to <strong>{{ accountForm.email || 'your email' }}</strong></span>
            </div>
            <div class="account-info-row">
              <v-icon small color="#74a533">mdi-shield-check-outline</v-icon>
              <span>No spam — just your booking updates</span>
            </div>
            <div class="account-info-row">
              <v-icon small color="#74a533">mdi-lightning-bolt-outline</v-icon>
              <span>Faster check-out on future visits</span>
            </div>
          </div>
          <v-text-field
            v-model="accountForm.email"
            label="Account Email"
            readonly
            outlined
            dense
            prepend-inner-icon="mdi-email"
          ></v-text-field>
          <v-alert v-if="accountError" type="error" dense class="mt-2" dismissible @input="accountError = ''">
            {{ accountError }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="account-modal-actions">
          <v-btn text small @click="skipAccountCreation" :disabled="accountCreating">
            Continue as Guest
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed small @click="createAccount" :loading="accountCreating">
            <v-icon left small>mdi-account-check-outline</v-icon>
            Create My Account
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <transition name="fade">
      <div class="sidebar-overlay" v-if="sidebarOpen" @click="closeSidebar"></div>
    </transition>
    <transition name="slide">
      <div class="sidebar-wrapper" v-show="sidebarOpen">
        <div class="sidebar-header">
          <h3 class="sidebar-title">
            <v-icon small class="mr-1">mdi-cart</v-icon>
            Your Booking Cart
            <span v-if="cartCount > 0" class="sidebar-badge">{{ cartCount }}</span>
          </h3>
          <v-btn icon small @click="closeSidebar" class="close-sidebar-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <side-bar ref="sidebar" @close="closeSidebar"></side-bar>
      </div>
    </transition>
  </div>
</template>

<script>
import FrontNavigation from '../Repeated/FrontNavigation'
import SideBar from '../Repeated/SideBar.vue'

export default {
  name: 'BookingInformation',
  components: { FrontNavigation, SideBar },

  data() {
    return {
      requiredRules: [(v) => (v !== undefined && v !== null && String(v).trim() !== '') || 'This field is required'],
      emailRules: [
        (v) => !!v || 'E-mail is required',
        (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid'
      ],
      phoneRules: [
        (v) => !!v || 'Phone is required',
        (v) => String(v).startsWith('+') || 'Phone must start with + and country code',
        (v) => String(v).replace(/[^0-9]/g, '').length >= 11 || 'Phone number is too short'
      ],
      valid: true,
      residency_proof: false,
      showDisclaimer: false,
      showAccountModal: false,
      accountCreating: false,
      loading: false,
      accountError: '',
      processingBooking: false,
      showMissingWarning: false,
      currentBookingId: null,
      entrySectionExpanded: false,
      accountDecision: null,
      sidebarOpen: false,
      cartCount: 0,

      booking: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        code: '',
        country: '',
        city: '',
        additional_note: '',
        promo_code: '',
        newsletter: false,
        country_details: {},
        entry_mode: '',
        entry_point: '',
        mode_registration: '',
        terms: false,
        disclaimer: false,
        type: 'GB601X10'
      },

      accountForm: { email: '' },

      donation: {
        amount: '',
        currency: '',
        donation_to: '',
        cause: '',
        currency_name: ''
      }
    }
  },

  created() {
    this.$store.dispatch('get_all_countries')
    this.$store.dispatch('get_full_conservation_fees')
    this.$store.dispatch('get_all_gates')
    this.$store.dispatch('get_all_entry_modes')
    this.updateCartCount()
    this.$root.$on('cart-updated', this.updateCartCount)
  },

  beforeDestroy() {
    this.$root.$off('cart-updated', this.updateCartCount)
  },

  computed: {
    allCountries() {
      return (this.$store.state.CountryStore && this.$store.state.CountryStore.all_countries) || []
    },
    allGates() {
      return (this.$store.state.GateStore && this.$store.state.GateStore.all_gates) || []
    },
    allEntryPoints() {
      const allEntry = this.allGates || []
      const res = []
      if (this.booking.entry_mode) {
        for (let i = 0; i < allEntry.length; i++) {
          if (allEntry[i].entry_mode_public_id === this.booking.entry_mode) {
            res.push(allEntry[i])
          }
        }
      }
      return res
    },
    missingRequirements() {
      const list = []
      if (!this.booking.first_name) list.push('Enter your <strong>first name</strong>')
      if (!this.booking.last_name) list.push('Enter your <strong>last name</strong>')
      if (!this.isEmailValid(this.booking.email)) list.push('Enter a valid <strong>email address</strong>')
      if (!this.booking.code) list.push('Select your <strong>country</strong>')
      if (!this.isPhoneValid(this.booking.phone)) list.push('Enter a valid <strong>phone</strong> starting with + and country code')
      if (!this.booking.city) list.push('Enter your <strong>city</strong>')
      if (!this.booking.terms) list.push('Accept the <strong>terms and conditions</strong>')
      if (!this.booking.disclaimer) list.push('Accept the <strong>liability disclaimer</strong>')
      return list
    },
    bookingCheckIn() {
      try {
        const booking = JSON.parse(localStorage.getItem('booking') || '{}')
        return booking.check_in || ''
      } catch (e) { return '' }
    },
    bookingCheckOut() {
      try {
        const booking = JSON.parse(localStorage.getItem('booking') || '{}')
        return booking.check_out || ''
      } catch (e) { return '' }
    },
    bookingNights() {
      const ci = this.bookingCheckIn
      const co = this.bookingCheckOut
      if (ci && co) {
        return Math.max(1, this.$moment(co).diff(this.$moment(ci), 'days'))
      }
      return 1
    },
    chosenFacilities() {
      try { return JSON.parse(localStorage.getItem('facility_data') || '[]') } catch (e) { return [] }
    },
    chosenInventory() {
      try { return JSON.parse(localStorage.getItem('activity_data') || '[]') } catch (e) { return [] }
    },
    allConservationFees() {
      try { return JSON.parse(localStorage.getItem('conservation_data') || '[]') } catch (e) { return [] }
    },
    allVehicles() {
      try { return JSON.parse(localStorage.getItem('vehicle_data') || '[]') } catch (e) { return [] }
    },
    proofOfResidency() {
      try { return JSON.parse(localStorage.getItem('residency_proof_data') || '[]') } catch (e) { return [] }
    },
    chosenExternalFacilities() {
      try { return JSON.parse(localStorage.getItem('external_facility_data') || '[]') } catch (e) { return [] }
    }
  },

  watch: {
    'booking.phone'(val) {
      if (!this.booking.code) return
      const country = this.allCountries.find((c) => c.code === this.booking.code)
      if (!country || !country.calling_code) return
      const prefix = '+' + country.calling_code
      if (!val || !val.startsWith(prefix)) {
        this.booking.phone = prefix
      }
    }
  },

  methods: {
    updateCartCount() {
      let count = 0
      try {
        const guests = JSON.parse(localStorage.getItem('guests') || '{"adults":0,"children":0}')
        if ((guests.adults || 0) + (guests.children || 0) > 0) count++
      } catch (e) {}
      try {
        const v = JSON.parse(localStorage.getItem('vehicle_data') || '[]')
        const hasV = v.some((x) => Number(x.vehicles) > 0)
        const vSep = JSON.parse(localStorage.getItem('vehicle_separate') || 'false')
        if (hasV || vSep) count++
      } catch (e) {}
      try { if (JSON.parse(localStorage.getItem('activity_data') || '[]').length > 0) count++ } catch (e) {}
      try { if (JSON.parse(localStorage.getItem('facility_data') || '[]').length > 0) count++ } catch (e) {}
      this.cartCount = count
    },

    openCartSidebar() {
      this.sidebarOpen = true
      document.body.style.overflow = 'hidden'
    },
    closeSidebar() {
      this.sidebarOpen = false
      document.body.style.overflow = ''
    },

    isEmailValid(v) { return !!v && /.+@.+\..+/.test(v) },
    isPhoneValid(v) {
      if (!v) return false
      if (!String(v).startsWith('+')) return false
      return String(v).replace(/[^0-9]/g, '').length >= 11
    },
    guardPhonePrefix(e) {
      if (!this.booking.code) return
      const country = this.allCountries.find((c) => c.code === this.booking.code)
      if (!country || !country.calling_code) return
      const prefix = '+' + country.calling_code
      const input = e.target
      const selStart = input.selectionStart
      const selEnd = input.selectionEnd
      if (e.key === 'Backspace' && selStart <= prefix.length && selStart === selEnd) e.preventDefault()
      if (e.key === 'Delete' && selStart < prefix.length) e.preventDefault()
    },
    findCountryCode(data) {
      this.allCountries.forEach((country) => {
        if (data === country.code) {
          this.booking.phone = '+' + country.calling_code
          this.booking.country_details = country
        }
      })
    },
    addProof() { this.residency_proof = false },
    findEntryPoints() { return this.allEntryPoints },
    openDisclaimer() { this.showDisclaimer = true },
    acceptDisclaimer() { this.booking.disclaimer = true; this.showDisclaimer = false },

    postBooking() {
      if (this.processingBooking) return

      if (!this.bookingCheckIn || !this.bookingCheckOut) {
        if (this.$toast) this.$toast.error('Booking dates are missing. Please go back and select dates.')
        return
      }

      this.showMissingWarning = true
      const formValid = this.$refs.form.validate()
      const termsOk = this.booking.terms && this.booking.disclaimer
      if (!formValid || !termsOk) {
        if (this.$toast) this.$toast.warning('Please complete the highlighted fields before submitting.')
        this.$nextTick(() => {
          const el = document.querySelector('.warning-block')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
        return
      }
      this.processingBooking = true
      this.loading = true
      this.accountCreating = false
      this.accountError = ''
      this.accountForm.email = this.booking.email
      this.accountDecision = null
      this.showAccountModal = true
    },

    buildBookingPayload() {
      const guests = (this.allConservationFees || []).map((g) => ({
        payment_public_id: g.payment_public_id,
        payment_person: g.payment_person,
        payment_guests: String(g.payment_guests || g.guest_count || 0),
        payment_schedule: g.payment_schedule,
        payment_person_discount: g.payment_person_discount || 0,
        reason: g.reason || null
      }))
      const facilities = (this.chosenFacilities || []).map((f) => Object.assign({}, f))
      const inventory = (this.chosenInventory || []).map((i) => Object.assign({}, i))
      const vehicles = (this.allVehicles || []).map((v) => ({
        vehicle_charge_public_id: v.vehicle_charge_public_id,
        vehicles: String(v.vehicles || v.vehicle_count || 0),
        discount: v.discount || 0,
        reason: v.reason || null
      }))
      const external_facilities = (this.chosenExternalFacilities || []).map((e) => Object.assign({}, e))
      const proof_of_residency = (this.proofOfResidency || []).map((p) => Object.assign({}, p))

      const combinedData = Object.assign(
        JSON.parse(localStorage.getItem('user_data') || '{}'),
        this.booking
      )

      combinedData.check_in = this.bookingCheckIn
      combinedData.check_out = this.bookingCheckOut
      combinedData.days = this.bookingNights

      combinedData.type = combinedData.type || 'GB601X10'
      combinedData.currency = this.$store.state.CurrencyStore.browser_currency_id
      combinedData.currency_id = this.$store.state.CurrencyStore.browser_currency_id
      combinedData.session_id =
        localStorage.getItem('session_token') ||
        localStorage.getItem('lkjhgfdsa') ||
        'guest'

      return Object.assign({}, combinedData, {
        guests,
        facilities,
        inventory,
        vehicles,
        external_facilities,
        proof_of_residency,
        donation: this.donation || {}
      })
    },

    processBookingAfterDecision() {
      if (!this.$store.state.CurrencyStore || !this.$store.state.CurrencyStore.browser_currency_id) {
        if (this.$toast) this.$toast.error('Currency information is missing. Please refresh the page and try again.')
        this.$store.dispatch('loading_false')
        this.loading = false
        this.processingBooking = false
        this.showAccountModal = false
        return
      }
      this.$store.dispatch('loading_true')
      this.loading = true
      const data = this.buildBookingPayload()
      if (this.accountDecision === 'created') {
        this.createUserAccountThenBooking(data)
      } else {
        this.submitBookingOnly(data)
      }
    },

    createUserAccountThenBooking(bookingData) {
      this.accountCreating = true
      const userAccountData = {
        first_name: this.booking.first_name,
        last_name: this.booking.last_name,
        email: this.booking.email,
        phone_number: this.booking.phone,
        user_type: 'acc1886a',
        has_account: true,
        session_id: bookingData.session_id || 'guest',
        customer_code_kes: '0000',
        customer_code_usd: '0000'
      }

      this.$store
        .dispatch('post_user_guest', userAccountData)
        .then((userResponse) => {
          if (userResponse && userResponse.data) {
            if (userResponse.data.id) bookingData.user_id = userResponse.data.id
            if (userResponse.data.public_id) bookingData.user_public_id = userResponse.data.public_id
            if (userResponse.data.token || userResponse.data.access_token) {
              localStorage.setItem('auth_token', userResponse.data.token || userResponse.data.access_token)
            }
          }
          if (this.$toast) this.$toast.success('Account created successfully.')
          return true
        })
        .catch((error) => {
          if (this.$toast) this.$toast.warning('Proceeding with your booking (account step skipped).')
          return true
        })
        .then(() => {
          return this.$store.dispatch('post_booking_public', bookingData)
        })
        .then((bookingResponse) => {
          this.handleBookingSuccess(bookingResponse, true)
        })
        .catch((error) => {
          this.handleBookingError(error)
        })
        .finally(() => {
          this.accountCreating = false
        })
    },

    submitBookingOnly(bookingData) {
      this.$store
        .dispatch('post_booking_public', bookingData)
        .then((bookingResponse) => { this.handleBookingSuccess(bookingResponse, false) })
        .catch((error) => { this.handleBookingError(error) })
    },

    handleBookingSuccess(response, accountCreated) {
      const publicId = response && response.data && response.data.data && response.data.data.booking_public_id

      if (!publicId) {
        this.handleBookingError({ message: 'Booking response was missing booking_public_id.' })
        return
      }

      const message = accountCreated
        ? 'Booking submitted successfully!'
        : 'Booking submitted successfully!'
      if (this.$toast) this.$toast.success(message)

      localStorage.setItem('previous_booking', JSON.stringify(response.data))
      localStorage.setItem('current_booking_id', publicId)
      this.currentBookingId = publicId

      this.processingBooking = false
      this.loading = false
      this.$store.dispatch('loading_false')
      this.showAccountModal = false

      this.$emit('payment-complete', response.data)
      this.$router.push(`/payment?booking_id=${publicId}`)
    },

    handleBookingError(error) {
      let errorMessage = 'Booking failed. Please try again.'
      if (error && error.response) {
        if (error.response.data) {
          if (typeof error.response.data === 'string') errorMessage = error.response.data
          else if (error.response.data.messages) {
            errorMessage = Array.isArray(error.response.data.messages)
              ? error.response.data.messages.join('; ')
              : String(error.response.data.messages)
          } else if (error.response.data.message) {
            errorMessage = Array.isArray(error.response.data.message)
              ? error.response.data.message.join('; ')
              : error.response.data.message
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error
          }
        }
      } else if (error && error.request) {
        errorMessage = 'No response from server. Please check your connection.'
      } else {
        errorMessage = (error && error.message) || 'Something went wrong. Please try again.'
      }
      this.processingBooking = false
      this.loading = false
      this.accountCreating = false
      this.$store.dispatch('loading_false')
      if (this.showAccountModal) this.accountError = errorMessage
      else if (this.$toast) this.$toast.error(errorMessage)
    },

    createAccount() {
      if (this.accountCreating) return
      if (!this.accountForm.email || !/.+@.+\..+/.test(this.accountForm.email)) {
        this.accountError = 'A valid email is required to create your account'
        if (this.$toast) this.$toast.error(this.accountError)
        return
      }
      this.accountCreating = true
      this.accountError = ''
      this.accountDecision = 'created'
      this.showAccountModal = false
      if (this.$toast) this.$toast.success('Creating your account and booking...')
      this.processBookingAfterDecision()
    },

    skipAccountCreation() {
      if (this.accountCreating) return
      this.accountDecision = 'skipped'
      this.accountError = ''
      this.showAccountModal = false
      if (this.$toast) this.$toast.info('Continuing as guest. You can create an account later.')
      this.processBookingAfterDecision()
    }
  }
}
</script>

<style scoped>
@font-face {
  font-family: 'Earthwerk';
  src: url('PizzaDude.dk-Earthwerk.otf') format('opentype');
  font-weight: normal;
  font-style: normal;
}

.new-booking-page {
  background: transparent;
  min-height: 100vh;
  padding: 24px 16px 40px;
  position: relative;
}
.app-container { max-width: 1000px; margin: 0 auto; }

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  margin-top: 10px;
}
.header-text h2 {
  color: #2d4a1e;
  font-size: 28px;
  margin: 0 0 4px 0;
  text-shadow: 0 1px 3px rgba(255,255,255,0.5);
}
.subtitle { color: #5e6e4e; font-size: 14px; margin: 0; }

.cart-btn {
  position: relative;
  width: 50px; height: 50px;
  background: #74a533;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(116,165,51,0.3);
  cursor: pointer;
  transition: transform 0.2s;
  flex-shrink: 0;
}
.cart-btn:hover { transform: scale(1.05); }
.cart-badge {
  position: absolute;
  top: -4px; right: -4px;
  background: #ff5252;
  color: #fff;
  font-size: 12px; font-weight: bold;
  width: 22px; height: 22px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #fff;
}

.earthwerk-font { font-family: 'Earthwerk', sans-serif; text-transform: uppercase; letter-spacing: 1px; }

.section-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}
.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #2d4a1e;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 16px 0;
  font-family: 'Earthwerk', sans-serif;
}
.section-title.clickable { cursor: pointer; user-select: none; }
.section-hint { font-size: 11px; color: #7b8f6b; }

.warning-block {
  background: #fff8e1;
  border-left: 4px solid #ff9800;
}
.warning-head { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; color: #b26a00; }
.missing-list { list-style: disc; padding-left: 22px; margin: 0; color: #8a5300; font-size: 13px; line-height: 1.6; }

.terms-section { background: #f9fbf6; border: 1px solid #e8f0dd; }
.terms-item { margin-bottom: 14px; }
.terms-item:last-child { margin-bottom: 0; }
.terms-item.terms-item-error { background: rgba(255,152,0,0.08); border-left: 4px solid #ff9800; padding: 8px; border-radius: 6px; }
.terms-link {
  display: flex; align-items: center; gap: 8px;
  color: #74a533;
  font-size: 13px;
  margin-bottom: 6px;
  cursor: pointer;
  text-decoration: none;
}
.terms-link:hover { text-decoration: underline; }
.terms-error-msg {
  display: flex; align-items: center; gap: 6px;
  color: #e65100;
  font-size: 12.5px;
  margin-top: 4px;
}

.action-section {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  margin-top: 10px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  display: flex; flex-direction: column; gap: 10px;
}
.app-continue-btn {
  width: 100%;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  background: #74a533;
  color: #fff;
  font-family: 'Earthwerk', sans-serif;
  font-size: 16px; font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 16px 20px;
  border: none; border-radius: 40px; cursor: pointer;
  box-shadow: 0 4px 12px rgba(116,165,51,0.3);
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}
.app-continue-btn:active { transform: scale(0.98); }
.app-continue-btn:disabled { opacity: 0.7; cursor: not-allowed; }

.dialog-header {
  background: #74a533;
  color: #fff;
  padding: 16px 20px;
  display: flex; align-items: center;
  font-family: 'Earthwerk', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.dialog-actions { padding: 14px 20px; border-top: 1px solid #eee; }

.disclaimer-card, .account-card, .residency-dialog { border-radius: 16px !important; overflow: hidden; }
.disclaimer-body {
  padding: 20px !important;
  max-height: 60vh;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.55;
  color: #333;
}
.disclaimer-body p { margin-bottom: 8px; }
.disclaimer-list { padding-left: 20px; margin: 6px 0; }
.disclaimer-list li { margin-bottom: 4px; }

.proof-item {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 12px;
}

.account-modal-body { padding: 20px !important; }
.account-description { color: #444; font-size: 13.5px; line-height: 1.5; margin-bottom: 14px; }
.account-info-block { background: #fafafa; border: 1px solid #eee; border-radius: 8px; padding: 12px 14px; margin-bottom: 14px; }
.account-info-row { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #555; padding: 5px 0; }
.account-info-row:not(:last-child) { border-bottom: 1px dashed #e8e8e8; }
.account-modal-actions { padding: 12px 20px 16px; display: flex; align-items: center; gap: 8px; }

.sidebar-overlay {
  position: fixed; top: 0; right: 0; bottom: 0; left: 0;
  background: rgba(15,23,33,0.55);
  z-index: 999;
  backdrop-filter: blur(1px);
}
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

.sidebar-wrapper {
  position: fixed;
  top: 0; right: 0;
  width: 380px; max-width: 92vw;
  height: 100vh;
  background: #fff;
  box-shadow: -6px 0 28px rgba(0,0,0,0.14);
  z-index: 1000;
  display: flex; flex-direction: column; overflow-y: auto;
}
.slide-enter-active, .slide-leave-active { transition: transform 0.3s cubic-bezier(0.4,0,0.2,1); }
.slide-enter, .slide-leave-to { transform: translateX(100%); }

.sidebar-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px;
  background: #74a533; color: #fff;
  position: sticky; top: 0; z-index: 10;
}
.sidebar-title { font-size: 1.05rem; font-weight: 600; margin: 0; display: flex; align-items: center; gap: 8px; }
.sidebar-badge { background: #ff5252; color: #fff; border-radius: 999px; min-width: 22px; height: 22px; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; padding: 0 6px; margin-left: 8px; }
.close-sidebar-btn { color: #fff !important; background: rgba(255,255,255,0.18) !important; border-radius: 50% !important; }

@media (max-width: 960px) {
  .header-text h2 { font-size: 24px; }
}

@media (max-width: 600px) {
  .new-booking-page { padding: 16px 12px 32px; }
  .header-text h2 { font-size: 20px; }
  .subtitle { font-size: 12.5px; }
  .section-card { padding: 16px; border-radius: 12px; }
  .section-title { font-size: 13px; }
  .app-continue-btn { font-size: 14px; padding: 14px 18px; }

  .account-modal-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .account-modal-actions .v-btn { width: 100%; }
  .account-modal-actions .v-spacer { display: none; }

  .proof-item { grid-template-columns: 1fr; }

  .sidebar-wrapper {
    width: 100%; max-width: 100%;
    height: 85vh;
    top: auto; bottom: 0;
    border-top-left-radius: 20px; border-top-right-radius: 20px;
  }
  .slide-enter, .slide-leave-to { transform: translateY(100%); }
}

@media (max-width: 360px) {
  .header-text h2 { font-size: 18px; }
  .section-card { padding: 12px; }
}
</style>